import { useCallback, useEffect, useState, type ReactNode } from "react";
import { ArrowLeft, CheckCircle2, CircleDashed, FlaskConical, Send, ShieldCheck, XCircle } from "lucide-react";
import { Alert, Badge, Button, ErrorState, Field, LoadingState, Modal, Textarea } from "@/components/ui";
import { useContextApi, isAbortError, type CheckScore, type Proposal, type ProposalCheck } from "@/lib/contextApi";
import { cn } from "@/lib/utils";
import { humanizeKey, ValueView } from "./ContextCard";
import { SmartDiff } from "./LineDiff";
import { CodeBlock, formatDateTime, SectionTitle, StatusBadge, useErrorHandler } from "./shared";

/** Score values: means with 2 decimals; `correct` as count when integral, as percentage when a 0–1 fraction. */
function fmtScore(v: number | undefined | null, kind: "mean" | "correct"): string {
  if (v === undefined || v === null || !Number.isFinite(v)) return "–";
  if (kind === "mean") return v.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (Number.isInteger(v)) return String(v);
  if (Math.abs(v) <= 1) return `${(v * 100).toLocaleString("nl-NL", { maximumFractionDigits: 1 })}%`;
  return v.toLocaleString("nl-NL", { maximumFractionDigits: 2 });
}

function ScoreRow({ label, kind, check }: { label: string; kind: keyof CheckScore; check: ProposalCheck }) {
  const before = check.before?.[kind];
  const after = check.after?.[kind];
  const delta = typeof before === "number" && typeof after === "number" ? after - before : undefined;
  const sign = delta === undefined ? "" : delta > 0 ? "+" : delta < 0 ? "−" : "±";
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-2 font-mono tabular-nums">
        <span className="text-muted-foreground">{fmtScore(before, kind)}</span>
        <span className="text-muted-foreground">→</span>
        <span className="text-lg font-semibold">{fmtScore(after, kind)}</span>
        {delta !== undefined && (
          <span className={cn("text-sm font-semibold", delta > 0 ? "text-success" : delta < 0 ? "text-destructive" : "text-muted-foreground")}>
            {sign}
            {fmtScore(Math.abs(delta), kind)}
          </span>
        )}
      </div>
    </div>
  );
}

function Meta({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-0.5 truncate text-sm">{children}</div>
    </div>
  );
}

function ResultPreview({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === "") return <p className="text-xs text-muted-foreground">Geen resultaat.</p>;
  if (typeof value === "string") {
    const parsed = (() => {
      try {
        const p = JSON.parse(value);
        return typeof p === "object" && p !== null ? p : undefined;
      } catch {
        return undefined;
      }
    })();
    if (parsed !== undefined) return <ValueView value={parsed} />;
    return <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md border border-border bg-sl-grey-50 px-3 py-2 font-mono text-xs">{value}</pre>;
  }
  return (
    <div className="max-h-72 overflow-auto text-xs">
      <ValueView value={value} />
    </div>
  );
}

export function publishBlockReason(p: Proposal): string | null {
  if (p.status === "published") return "Dit voorstel is al gepubliceerd.";
  if (p.status === "rejected") return "Dit voorstel is afgewezen.";
  if (p.status !== "ready") return "Publiceren kan pas als de status ‘Klaar voor review’ is.";
  if (!p.proof?.length) return "Er is geen bewijs bij dit voorstel.";
  const missing = p.proof.filter((x) => !x.reproduced).length;
  if (missing) return `${missing} van ${p.proof.length} bewijsstukken ${missing === 1 ? "is" : "zijn"} niet gereproduceerd.`;
  return null;
}



function Chip({ ok, children, onClick }: { ok: boolean | null; children: ReactNode; onClick?: () => void }) {
  const cls = cn(
    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-medium",
    ok === true ? "border-[#CFE3B4] bg-[#EEF6E4] text-success" : ok === false ? "border-[#F6C4C6] bg-[#FDECEC] text-destructive" : "border-border bg-muted text-muted-foreground",
    onClick && "hover:underline",
  );
  const icon = ok === true ? <CheckCircle2 className="h-3.5 w-3.5" /> : ok === false ? <XCircle className="h-3.5 w-3.5" /> : <CircleDashed className="h-3.5 w-3.5" />;
  return onClick ? (
    <button type="button" onClick={onClick} className={cls}>
      {icon}
      {children}
    </button>
  ) : (
    <span className={cls}>
      {icon}
      {children}
    </span>
  );
}



export function ProposalDetail({
  id,
  onClose,
  onChanged,
  liveVersion,
  onOpenTable,
}: {
  id: string | null;
  onClose: () => void;
  onChanged: () => void;
  liveVersion?: string;
  onOpenTable?: (fqn: string) => void;
}) {
  const api = useContextApi();
  const handleError = useErrorHandler();
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: "success" | "danger"; text: string } | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    setNotice(null);
    setRejectOpen(false);
    setRejectReason("");
    setRejectError(null);
  }, [id]);

  useEffect(() => {
    if (!id) {
      setProposal(null);
      return;
    }
    const ac = new AbortController();
    setLoading(true);
    setLoadError(null);
    api
      .getProposal(id, ac.signal)
      .then((p) => setProposal(p))
      .catch((e) => {
        if (isAbortError(e)) return;
        setLoadError(handleError(e));
      })
      .finally(() => !ac.signal.aborted && setLoading(false));
    return () => ac.abort();
  }, [api, handleError, id, reload]);

  const refresh = useCallback(() => setReload((r) => r + 1), []);

  const publish = async () => {
    if (!proposal) return;
    setPublishing(true);
    setNotice(null);
    try {
      const res = await api.publishProposal(proposal.id);
      setNotice({ tone: "success", text: `Gepubliceerd${res?.published_version ? ` als versie ${res.published_version}` : ""}. Deze versie is nu live.` });
      onChanged();
      refresh();
    } catch (e) {
      const msg = handleError(e);
      if (msg) setNotice({ tone: "danger", text: `Publiceren mislukt: ${msg}` });
    } finally {
      setPublishing(false);
    }
  };

  const reject = async () => {
    if (!proposal || !rejectReason.trim()) return;
    setRejecting(true);
    setRejectError(null);
    try {
      await api.rejectProposal(proposal.id, rejectReason.trim());
      setRejectOpen(false);
      setRejectReason("");
      setNotice({ tone: "success", text: "Voorstel afgewezen." });
      onChanged();
      refresh();
    } catch (e) {
      const msg = handleError(e);
      if (msg) setRejectError(msg);
      else setRejectOpen(false);
    } finally {
      setRejecting(false);
    }
  };

  const p = proposal && proposal.id === id ? proposal : null;
  const blockReason = p ? publishBlockReason(p) : null;
  const final = p?.status === "published" || p?.status === "rejected";
  const nRepro = p?.proof?.filter((x) => x.reproduced).length ?? 0;
  const nProof = p?.proof?.length ?? 0;
  const delta = p?.check ? (p.check.after?.mean ?? NaN) - (p.check.before?.mean ?? NaN) : NaN;
  const baseIsLive = !!p?.base_version && p.base_version === liveVersion;
  const tables = p ? [...new Set((p.changes ?? []).map((c) => c.fqn))] : [];
  const jump = (sec: string) => document.getElementById(`voorstel-${sec}`)?.scrollIntoView({ behavior: "smooth", block: "start" });

  if (!id) return null;
  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="border-b border-border px-5 py-4 lg:px-6">
          <button type="button" onClick={onClose} className="mb-2 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground lg:hidden">
            <ArrowLeft className="h-4 w-4" />
            Alle voorstellen
          </button>
          {p ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge status={p.status} />
                <span className="text-sm text-muted-foreground">
                  {formatDateTime(p.created_at)}
                  {p.author ? ` · ${p.author}` : ""}
                </span>
              </div>
              <h2 className="mt-1.5 text-xl font-semibold leading-snug">{p.title}</h2>
              {p.reason && <p className="mt-1 max-w-[90ch] text-[15px] leading-relaxed text-muted-foreground">{p.reason}</p>}
              <p className="mt-2 text-sm">
                <span className="font-semibold">
                  {p.changes?.length ?? 0} wijziging{(p.changes?.length ?? 0) === 1 ? "" : "en"}
                </span>{" "}
                <span className="text-muted-foreground">in</span>{" "}
                {tables.map((t, i) => (
                  <span key={t}>
                    {i > 0 && ", "}
                    {onOpenTable ? (
                      <button type="button" className="font-mono text-[13px] font-medium text-info hover:underline" onClick={() => onOpenTable(t)}>
                        {t}
                      </button>
                    ) : (
                      <span className="font-mono text-[13px]">{t}</span>
                    )}
                  </span>
                ))}
                <span className="text-muted-foreground">: {[...new Set((p.changes ?? []).map((c) => humanizeKey(c.field)))].join(", ")}</span>
              </p>
            </>
          ) : (
            <h2 className="text-xl font-semibold">Voorstel</h2>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading && !p ? (
            <LoadingState label="Voorstel laden…" />
          ) : loadError ? (
            <ErrorState message={loadError} onRetry={refresh} />
          ) : !p ? null : (
            <div className="space-y-8 px-5 py-5 lg:px-6">
              {notice && (
                <Alert tone={notice.tone} onClose={() => setNotice(null)}>
                  {notice.text}
                </Alert>
              )}

              <div id="voorstel-wijzigingen" className="scroll-mt-4">
                <SectionTitle>
                  Wijzigingen <span className="font-normal normal-case tracking-normal">({p.changes?.length ?? 0})</span>
                </SectionTitle>
                {!p.changes?.length ? (
                  <p className="text-sm text-muted-foreground">Geen wijzigingen.</p>
                ) : (
                  <div className="space-y-5">
                    {p.changes.map((c, i) => (
                      <div key={`${c.fqn}-${c.field}-${i}`} className="space-y-1.5">
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <span className="font-medium">{humanizeKey(c.field)}</span>
                          <span className="text-muted-foreground">in</span>
                          <span className="break-all font-mono text-[13px]">{c.fqn}</span>
                        </div>
                        <SmartDiff before={c.before} after={c.after} mode="split" />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div id="voorstel-bewijs" className="scroll-mt-4">
                <SectionTitle icon={<ShieldCheck className="h-3.5 w-3.5" />}>
                  Bewijs <span className="font-normal normal-case tracking-normal">({p.proof?.length ?? 0})</span>
                </SectionTitle>
                {!p.proof?.length ? (
                  <p className="text-sm text-muted-foreground">Geen bewijs aangeleverd.</p>
                ) : (
                  <ol className="space-y-3">
                    {p.proof.map((pr, i) => (
                      <li key={i} className="rounded-md border border-border p-3">
                        <div className="flex items-start justify-between gap-3">
                          <p className="text-sm font-medium">
                            <span className="mr-1.5 text-muted-foreground">{i + 1}.</span>
                            {pr.claim}
                          </p>
                          {pr.reproduced ? (
                            <Badge tone="success">
                              <CheckCircle2 className="h-3 w-3" />
                              Gereproduceerd
                            </Badge>
                          ) : (
                            <Badge tone="danger">
                              <XCircle className="h-3 w-3" />
                              Niet gereproduceerd
                            </Badge>
                          )}
                        </div>
                        {pr.sql && <CodeBlock code={pr.sql} className="mt-2" maxHeight={260} />}
                        <div className="mt-2">
                          <div className="mb-1 text-xs font-medium text-muted-foreground">Resultaat</div>
                          <ResultPreview value={pr.result_preview} />
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              <div id="voorstel-check" className="scroll-mt-4">
                <SectionTitle icon={<FlaskConical className="h-3.5 w-3.5" />}>Check</SectionTitle>
                {!p.check ? (
                  <p className="text-sm text-muted-foreground">Nog geen check.</p>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <ScoreRow label="Gemiddelde score" kind="mean" check={p.check} />
                      <ScoreRow label="Correct" kind="correct" check={p.check} />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      De vragenlijst <span className="font-mono text-foreground">{p.check.dataset || "–"}</span> ({p.check.n ?? "–"} vragen) is gedraaid met de huidige kaart (voor) en
                      met deze wijziging (na). Score: gemiddelde beoordeling op schaal 1–5; correct: aantal vragen met een juist antwoord. Per-vraag verschillen (verbeterd,
                      gelijk, verslechterd) levert de check niet mee.
                    </p>
                  </div>
                )}
              </div>

              <div id="voorstel-impact" className="scroll-mt-4 rounded-lg border border-border p-4">
                <SectionTitle>Publicatie-impact</SectionTitle>
                <div className="flex flex-wrap gap-2 text-[13px]">
                  <Chip ok={nProof > 0 && nRepro === nProof} onClick={nProof ? () => jump("bewijs") : undefined}>
                    {nProof ? `${nRepro}/${nProof} bewijs gereproduceerd` : "Geen bewijs"}
                  </Chip>
                  <Chip ok={p.check ? delta >= 0 : null} onClick={p.check ? () => jump("check") : undefined}>
                    {p.check
                      ? `Score ${fmtScore(p.check.before?.mean, "mean")} → ${fmtScore(p.check.after?.mean, "mean")} (schaal 1–5, ${p.check.n} vragen)`
                      : "Nog geen check"}
                  </Chip>
                  <Chip ok={p.base_version ? baseIsLive : null}>{p.base_version ? (baseIsLive ? "Gebaseerd op live versie" : "Basis is niet meer live") : "Basis onbekend"}</Chip>
                </div>
                <p className="mt-2 text-[13px] text-muted-foreground">
                  Raakt:{" "}
                  {tables.map((t, i) => (
                    <span key={t}>
                      {i > 0 && ", "}
                      {onOpenTable ? (
                        <button type="button" className="font-mono font-medium text-info hover:underline" onClick={() => onOpenTable(t)}>
                          {t}
                        </button>
                      ) : (
                        <span className="font-mono">{t}</span>
                      )}
                    </span>
                  ))}
                  <span className="ml-2 font-mono text-xs">· {p.id}</span>
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Nu live: <span className="font-mono text-foreground">{liveVersion ?? "onbekend"}</span>. Publiceren bouwt een nieuwe versie op basis van{" "}
                  <span className="font-mono text-foreground">{p.base_version ?? "?"}</span> plus deze wijzigingen en maakt die direct live voor alle agents.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 border-t border-border pt-5 sm:grid-cols-3">
                <Meta label="Basisversie">
                  <span className="font-mono text-xs">{p.base_version || "–"}</span>
                </Meta>
                <Meta label="Gepubliceerde versie">
                  <span className="font-mono text-xs">{p.published_version || "–"}</span>
                </Meta>
              </div>
            </div>
          )}
        </div>

        {p && !final && (
          <div className="flex flex-col gap-2 border-t border-border bg-card px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:flex-row sm:items-center sm:justify-between lg:px-6">
            <p className="hidden text-sm text-muted-foreground sm:block">{blockReason ?? "Publiceren maakt direct een nieuwe live versie voor alle agents."}</p>
            <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
              <Button variant="outline" className="h-11 sm:h-10" onClick={() => setRejectOpen(true)} disabled={publishing}>
                <XCircle className="h-4 w-4" />
                Afwijzen
              </Button>
              <span title={blockReason ?? undefined} className="grid">
                <Button variant="primary" className="h-11 sm:h-10" onClick={publish} loading={publishing} disabled={!!blockReason}>
                  <Send className="h-4 w-4" />
                  Publiceren
                </Button>
              </span>
            </div>
          </div>
        )}
      </div>

      <Modal
        open={rejectOpen}
        onClose={() => !rejecting && setRejectOpen(false)}
        size="sm"
        title="Voorstel afwijzen"
        description="Leg kort uit waarom. De reden wordt bij het voorstel bewaard."
        footer={
          <>
            <Button variant="outline" onClick={() => setRejectOpen(false)} disabled={rejecting}>
              Annuleren
            </Button>
            <Button variant="danger" onClick={reject} loading={rejecting} disabled={!rejectReason.trim()}>
              Afwijzen
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {rejectError && <Alert tone="danger">{rejectError}</Alert>}
          <Field label="Reden" htmlFor="reject-reason" hint="Verplicht.">
            <Textarea id="reject-reason" rows={4} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} autoFocus />
          </Field>
        </div>
      </Modal>
    </>
  );
}
